<template>
  <div class="form-group container-fluid">
    <div class="row">
      <div class="col-md-8 left-side">
        <div class="form-group col">
          <label for="instruction-title">{{ $t('instructions.title') }}</label>
          <input class="form-control"
                 id="instruction-title"
                 name="product-name"
                 type="text"
                 v-model="editingInstruction.title"
                 v-validate="'required'">
        </div>

        <div class="row">
          <div class="col-md-12 drag-drop-container">
            <DragAndDropContainer ref="dragAndDropContainer"
                                  :numberOfFilesAllowed="1"
                                  :allowedFileSize="'5MB'"
                                  :showFailedToast="true"
                                  :showSuccessToast="false"
                                  :uploadedFiles="editingInstruction.files"
                                  :path="getInstructionFilePath"
                                  @filesChanged="filesChanged"
            ></DragAndDropContainer>
          </div>
        </div>


        <div class="row">
          <!-- file viewer is hidden on mobile devices  -->
          <div class="col-lg-12 d-none d-md-block">
            <div class="col-12 file-preview">
              <FileViewer v-if="fileInPreview" :file="fileInPreview">

              </FileViewer>
            </div>
          </div>
        </div>

      </div>
      <div class="col-md-4 right-side">
        <div class="form-group">
          <label for="instruction-description">{{ $t('instructions.description') }}</label>
          <textarea name="instruction-description" maxlength="255" v-model="editingInstruction.description"
                    id="instruction-description"
                    cols="30" rows="6"
                    class="form-control"></textarea>
          <span class="pull-right label label-default"
                :limitReached="getCharactersLeftOfDescription.used === getCharactersLeftOfDescription.limit"
                id="count_message">
            {{ getCharactersLeftOfDescription.used }}/{{ getCharactersLeftOfDescription.limit }}
          </span>

        </div>


        <div class="link-editor">
          <label for="instruction-description">{{ $t('instructions.addLinks') }}</label>
          <div class="row" v-bind:key="linkKey.key" v-for="(linkKey) in getLinkKeysOfCompany">
            <div class="key-selector col-md-12">

              <search-dropdown v-if="linkKey.key == 'workflow'"
                               :possibleValues="getActiveWorkflows"
                               :preSelectedValues="editingInstruction[linkKey.instructionProperty]"
                               :labelProperty="'name'"
                               :valueProperty="'id'"
                               :choiceLimit="0"
                               @changed="updateValuesOfKeys(linkKey, $event)"
                               :label="linkKey.label.translated ? $t(linkKey.label.value) : linkKey.label.value"
              ></search-dropdown>

              <search-dropdown v-else-if="linkKey.key == 'activity'"
                               :possibleValues="getActivitiesLowerCased"
                               :choiceLimit="0"
                               :preSelectedValues="editingInstruction[linkKey.instructionProperty]"
                               @changed="updateValuesOfKeys(linkKey, $event)"
                               :label="linkKey.label.translated ? $t(linkKey.label.value) : linkKey.label.value"
              ></search-dropdown>

              <template v-else>
                <div class="form-group">
                  <label :for="linkKey.key + '-input'">{{
                      linkKey.label.translated ? $t(linkKey.label.value) : linkKey.label.value
                    }}</label>
                  <div class="loading-tags" v-bind:style="{ display: existingTags.downloaded ? 'none' : 'inherit' }">
                    <ComponentLoadSpinner data-toggle="tooltip" data-placement="top"
                                          :title="$t('instructions.loadingExistingValues')" :loading="true"/>
                  </div>
                  <VoerroTagsInput :element-id="linkKey.key + '-input'"
                                   :id="linkKey.key + '-input'"
                                   :existing-tags="getExistingTags(linkKey)"
                                   :value-fields="'length'"
                                   :typeahead-activation-threshold="threshold"
                                   :typeahead-always-show="false"
                                   @focus="threshold = linkKey.threshold"
                                   @blur="threshold = 1"
                                   :typeahead-hide-discard="true"
                                   :typeahead-style="'dropdown'"
                                   v-model="tempTagArrays[linkKey.instructionProperty]"
                                   :limit="0"
                                   :placeholder="$t('instructions.typeAndEnterTag')"
                                   :add-tags-on-blur="false"
                                   @tag-added="tagsChanged($event, linkKey.instructionProperty)"
                                   @tag-removed="tagsChanged($event, linkKey.instructionProperty)"
                                   :hide-input-on-limit="true"
                                   :typeahead="true"></VoerroTagsInput>
                </div>
              </template>


            </div>
          </div>
          <div class="row form-mutation-buttons">
            <div class="col-12 text-right">
              <router-link :to="{name:'instructions'}" class="btn btn-full-width btn-cancel">
                {{ $t('ui.cancel') }}
              </router-link>
            </div>
            <div class="col-12 text-right">
              <button v-if="editingInstruction.id" @click="deleteInstruction()" class="btn btn-full-width btn-outline btn-danger mt-3"
                      type="button">
                {{ $t('ui.delete') }}
              </button>
            </div>
            <div class="col-12 text-right">
              <button @click="saveInstruction()" class="btn btn-full-width btn-primary mt-3" type="button">
                {{ editingInstruction.id ? $t('ui.save') : $t('ui.create') }}
              </button>
            </div>
          </div>

          <div class="row">
            <div class="col-12 file-owners">
        <span v-if="createdUser" class="created_by">
          {{ $t('instructions.created_at_by', {date: getCreatedByData.date, user: getCreatedByData.name}) }}
        </span>
              <span v-if="updatedUser" class="updated_by">
          {{ $t('instructions.updated_at_by', {date: getUpdatedByData.date, user: getUpdatedByData.name}) }}
        </span>
            </div>
          </div>

          <div v-if="getConfig.publications" class="publication-settings">
            <h4 class="publication-settings__title">{{ $t('instructions.publication.title') }}</h4>
            <p class="publication-settings__tooltip">{{ $t('instructions.publication.tooltip') }}</p>
            <div class="publication-settings__toggle">
              <span class="publication-settings__toggle-label">{{ $t('instructions.publication.toggleLabel') }}</span>
              <toggle-button v-model="editingInstruction.publishForOperators"
                             :color="{checked: '#00c9ff', unchecked: '#ADB4BD'}"
                             :labels="{checked: $t('instructions.publication.on'), unchecked: $t('instructions.publication.off')}"
                             :font-size="13"
                             :width="70"
                             :height="28"
                             :sync="true"
              />
            </div>
          </div>

          <div v-if="getConfig.publications && editingInstruction.id" class="instruction-history">
            <h4 class="instruction-history__title">{{ $t('instructions.publication.historyTitle') }}</h4>
            <div v-if="versions.length" class="instruction-history__list">
              <div v-for="version in versions" :key="version.id" class="instruction-history__entry">
                <div class="instruction-history__entry-header">
                  <span class="instruction-history__date">
                    {{ formatDate(version.created) }}
                    <template v-if="version.created_by_name"> &middot; {{ version.created_by_name }}</template>
                  </span>
                  <span :class="['instruction-history__status', 'instruction-history__status--' + version.status]">
                    {{ version.status === 'published' ? $t('instructions.publication.statusPublished') : $t('instructions.publication.statusDraft') }}
                  </span>
                </div>
                <span v-if="version.changelog" class="instruction-history__changelog">
                  {{ version.changelog }}
                </span>
              </div>
            </div>
            <p v-else class="instruction-history__empty">{{ $t('instructions.publication.noHistory') }}</p>
          </div>
        </div>

      </div>
    </div>

    <Modal v-if="getConfig.publications" :modalId="'publication-modal'" size="lg" @closed="onPublicationModalClosed">
      <template v-slot:body>
        <div class="publication-modal">
          <div class="publication-modal__header">
            <h5 class="publication-modal__title">{{ $t('instructions.publication.modal.title') }}</h5>
            <p class="publication-modal__description">{{ $t('instructions.publication.modal.description') }}</p>
          </div>
          <div class="publication-modal__changelog">
            <label class="publication-modal__changelog-label" for="changelog-input">
              {{ $t('instructions.publication.modal.changelogLabel') }}
            </label>
            <textarea id="changelog-input"
                      class="publication-modal__changelog-input"
                      v-model="changelog"
                      rows="7"
                      :placeholder="$t('instructions.publication.modal.changelogPlaceholder')"
            ></textarea>
          </div>
          <div class="publication-modal__actions">
            <button type="button" class="motivate-primary-button-outline" @click="saveWithoutPublishing">
              {{ $t('instructions.publication.modal.dontPublish') }}
            </button>
            <button type="button" class="motivate-primary-button" @click="saveAndPublish">
              <i class="fas fa-save"></i>
              {{ $t('instructions.publication.modal.publish') }}
            </button>
          </div>
        </div>
      </template>
    </Modal>




  </div>

</template>

<script>
import {mapGetters} from "vuex";
import pmtApi from "@/init/pmtApi";
import DragAndDropContainer from "@/components/filemanager/DragAndDropContainer";
import FileViewer from "@/components/filemanager/FileViewer";
import SearchDropdown from "@/components/SearchDropdown";
import VoerroTagsInput from '@voerro/vue-tagsinput';
import ComponentLoadSpinner from "@/components/general/ComponentLoadSpinner";
import Modal from "@/components/general/Modal";

export default {
  components: {SearchDropdown, ComponentLoadSpinner, FileViewer, DragAndDropContainer, VoerroTagsInput, Modal},
  mixins: [],
  name: "InstructionsForm",
  props: [
    'instruction'
  ],
  data() {
    return {
      threshold: 1, // Goes to 0 on focus and to 1 on blur => prevents dropdown to open after adding existing values
      createdUser: null,
      updatedUser: null,
      editingInstruction: {
        id: null,
        title: null,
        description: null,
        created_at: null,
        updated_at: null,
        created_by: null,
        updated_by: null,
        owner: null,
        type: 'file',
        numberOfEdits: 0,
        files: [],
        key0: [],
        key1: [],
        key2: [],
        key3: [],
        key4: [],
        publishForOperators: true
      },
      originalInstruction: null,
      filesModified: false,
      changelog: '',
      versions: [],
      fileInPreview: null,
      tempTagArrays: {
        key0: [],
        key1: [],
        key2: [],
        key3: [],
        key4: []
      },
      existingTags: {
        downloaded: false,
        key0: [],
        key1: [],
        key2: [],
        key3: [],
        key4: []
      }
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      return moment(dateStr).format('DD-MM-YYYY HH:mm:ss');
    },
    stringToArray(event) {
      const array = _.map(event.srcElement.value.split(','), value => {
        return _.trimStart(value)
      });
      return _.first(array) == '' ? [] : array;
    },
    updateValuesOfKeys(linkKey, event) {
      this.editingInstruction[linkKey.instructionProperty] = event;
    },
    async fetchCreatedByUser() {
      return this.$FUNCTIONS
          .Users("getUserById", this.editingInstruction.created_by)
          .then((res) => {
            this.createdUser = res.data;
          })
    },
    async getUpdatedByUser() {
      return this.$FUNCTIONS
          .Users("getUserById", this.editingInstruction.updated_by)
          .then((res) => {
            this.updatedUser = res.data;
          })
    },
    warningPopUp() {
      return this.$swal({
        icon: "warning",
        title: this.$t("ui.invalidForm"),
        text: this.$t("ui.invalidFormText"),
      });
    },
    tagsChanged($event, instructionProperty) {
      // Convert the tag objects back to an array of values so we can use it in firebase queries
      this.editingInstruction[instructionProperty] = _.map(this.tempTagArrays[instructionProperty], tag => {
        return tag.value;
      });
    },
    async deleteInstruction() {
      const deleteSwalResponse = await this.$swal({
        title: this.$t('instructions.deleteInstruction.title'),
        html: this.$t('instructions.deleteInstruction.message', {title: this.editingInstruction.title}),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('instructions.deleteInstruction.yes'),
        cancelButtonText: this.$t('instructions.deleteInstruction.no'),
        reverseButtons: true,
      });
      if (deleteSwalResponse.value) {
        await this.$store.dispatch('instructions/delete', this.editingInstruction.id);
        this.swalToastSuccess(this.$t('instructions.deletedInstruction'));
        this.$router.back();
      }
    },
    getExistingTags(linkKey) {
      return this.existingTags[linkKey.instructionProperty];
    },
    async saveInstruction() {
      if (!this.editingInstruction.key0.length
          && !this.editingInstruction.key1.length
          && !this.editingInstruction.key2.length
          && !this.editingInstruction.key3.length
          && !this.editingInstruction.key4.length) {
        return this.swalError(this.$t('instructions.noLinksSet'));
      }

      const result = await this.$validator.validateAll();
      if (!result) {
        return this.warningPopUp();
      }

      if (this.getConfig.publications
          && this.editingInstruction.id
          && this.editingInstruction.publishForOperators
          && this.hasPublicationChanges()) {
        return this.showPublicationModal();
      }

      const publishStatus = this.getConfig.publications && this.editingInstruction.publishForOperators ? 'published' : 'draft';
      await this.performSave(publishStatus, null);
    },
    async performSave(publishStatus, changelog) {
      this.$store.commit('setLoading', true);
      try {
        const uploadResults = await this.$refs.dragAndDropContainer.uploadFiles();
        if (uploadResults.error) {
          this.$store.commit('setLoading', false);
          return;
        } else if (!uploadResults.length) {
          this.swalError(this.$t('instructions.noFiledUploaded'))
          this.$store.commit('setLoading', false);
          return;
        }

        this.editingInstruction.files = [];
        uploadResults.forEach(file => {
          this.editingInstruction.files.push({
            name: file.metadata.name,
            type: file.metadata.contentType,
            bucket: file.metadata.bucket,
            fullPath: file.metadata.fullPath,
            color: file.metadata.color ? file.metadata.color : null
          })
        });
        // Create a second array with only file hashes => to perform the array contains query on
        this.editingInstruction.filePaths = _.map(this.editingInstruction.files, file => {
          return file.fullPath
        });

        const res = await this.$store.dispatch('instructions/set', {
          ...this.editingInstruction,
          companyId: this.getClaims.companyId,
          publishStatus,
          changelog,
        });
        this.editingInstruction.id = res;

        if (this.getConfig.publications) {
          try {
            await pmtApi.post('/instructions/versions', {
              firebaseId: this.editingInstruction.id,
              publishStatus,
              changelog,
              name: this.editingInstruction.title,
            });
          } catch (e) {
            console.error("Failed to create instruction version record", e);
          }
        }

        this.$router.back();
      } catch (e) {
        console.error("upload error, cancel instruction update", e);
      }

      this.$store.commit('setLoading', false);
    },
    filesChanged(event) {
      this.fileInPreview = _.first(event);
      this.filesModified = true;
      if (!this.editingInstruction.title && this.fileInPreview) {
        this.updateTitle(this.fileInPreview.file ? this.fileInPreview.file.name : this.fileInPreview.name);
        this.swalToastInfo(this.$t('instructions.titleChangedToFileName'))
      }
    },
    updateTitle(title) {
      this.editingInstruction.title = title;
    },
    hasPublicationChanges() {
      if (!this.originalInstruction) return false;
      const toggledOn = !this.originalInstruction.publishForOperators && this.editingInstruction.publishForOperators;
      const titleChanged = this.originalInstruction.title !== this.editingInstruction.title;
      const descriptionChanged = this.originalInstruction.description !== this.editingInstruction.description;
      return toggledOn || titleChanged || descriptionChanged || this.filesModified;
    },
    showPublicationModal() {
      this.changelog = '';
      window.$('#publication-modal').modal('show');
    },
    hidePublicationModal() {
      window.$('#publication-modal').modal('hide');
    },
    onPublicationModalClosed() {
      this.changelog = '';
    },
    async saveAndPublish() {
      this.hidePublicationModal();
      await this.performSave('published', this.changelog);
    },
    async saveWithoutPublishing() {
      this.hidePublicationModal();
      await this.performSave('draft', null);
    },
    getLocalPossibleValues(key, maxAgeInDays = 0) {
      const savedObjectString = localStorage.getItem(`temp_values_${key}`);
      if (savedObjectString) {
        const savedObject = JSON.parse(savedObjectString);
        // Check if the saved object is not older than the max age in days => then return values
        if (moment(savedObject.date, 'YYYY-MM-DD').isSameOrAfter(moment().subtract(maxAgeInDays, 'day'), 'day')) {
          return savedObject.values;
        } else {
          // Remove the old data
          localStorage.removeItem(`temp_values_${key}`)
          return null;
        }
      } else {
        return null;
      }
    },
    setLocalPossibleValues(key, values) {
      const savingObjectString = {
        date: moment().format('YYYY-MM-DD'),
        key,
        values
      };
      localStorage.setItem(`temp_values_${key}`, JSON.stringify(savingObjectString));
    },
    async fetchPossibleValues() {
      this.existingTags.downloaded = false;

      // Get all possible values in bigquery for all keys that have a bigquery source
      // We get ALL values and do an uniqueBy in the front-end
      // This way we only need to run the BQ query once and get all values
      // If we would do distinct in the query: we would need to run the query per needed key
      // Making it way more expensive
      const keysToFetch = [];
      _.forEach(this.getConfig.instructions.linkKeys, linkKey => {
        if (linkKey.source === 'BIGQUERY') {
          // Bigquery source => check if there's local storage data
          const localValues = this.getLocalPossibleValues(linkKey.instructionProperty, 0);
          if (localValues) {
            // If there's local data: we don't call the api again
            // But we save the local values
            this.existingTags[linkKey.instructionProperty] = localValues;
          } else {
            // If there's no local data: we should fetch it
            keysToFetch.push(linkKey)
          }
        } else {
          // Not bigquery source => so we don't fetch it from bigquery
        }
      })
      if (keysToFetch.length) {
        const res = await this.$FUNCTIONS
            .Instructions("getPossibleValues", _.map(keysToFetch, linkKey => {
              return linkKey.key
            })).catch(e => {
              return {data: []}
            });
        keysToFetch.forEach(bigQueryKey => {
          this.existingTags[bigQueryKey.instructionProperty] = _.map(_.filter(_.uniqBy(res.data, bigQueryKey.key), uniqueBigQueryKeyValue => {
            return !!uniqueBigQueryKeyValue[bigQueryKey.key] && !!uniqueBigQueryKeyValue[bigQueryKey.key].trim();
          }), uniqueBigQueryKeyValueThatIsNotNull => {
            return {key: uniqueBigQueryKeyValueThatIsNotNull[bigQueryKey.key], value: uniqueBigQueryKeyValueThatIsNotNull[bigQueryKey.key]}
          });
          // Save the newly downloaded values
          this.setLocalPossibleValues(bigQueryKey.instructionProperty, this.existingTags[bigQueryKey.instructionProperty] )
        });
      }

      this.existingTags.downloaded = true;
    },
  },
  computed: {
    ...mapGetters('company', ['getConfig', 'getActivitiesLowerCased']),
    ...mapGetters("users", ["getClaims", "getMe"]),
    ...mapGetters('workflows', ['getActiveWorkflows', 'getWorkflowName']),
    getLinkKeysOfCompany() {
      return this.getConfig.instructions ? this.getConfig.instructions.linkKeys : [];
    },
    getCreatedByData() {
      return {
        name: this.createdUser.displayName ? this.createdUser.displayName : this.createdUser.email,
        date: moment(this.editingInstruction.created_at).format('DD-MM-YYYY HH:mm:ss')
      }
    },
    getUpdatedByData() {
      return {
        name: this.updatedUser.displayName ? this.updatedUser.displayName : this.updatedUser.email,
        date: moment(this.editingInstruction.updated_at).format('DD-MM-YYYY HH:mm:ss')
      }
    },
    getCharactersLeftOfDescription() {
      return {
        limit: 255,
        used: this.editingInstruction.description ? this.editingInstruction.description.length : 0
      }
    },
    getLinkKeysKeys() {
      return _.map(this.getLinkKeysOfCompany, linkKey => {
        return linkKey.key;
      })
    },
    getInstructionFilePath() {
      const companyId = this.getClaims.companyId;
      return `instructions/${companyId}`;
    },
  },
  created() {
    // Enable tooltips
    $("body").tooltip({selector: '[data-toggle=tooltip]'});
    if (this.instruction) {
      this.editingInstruction = _.cloneDeep(this.instruction);

      if (this.editingInstruction.publishForOperators === undefined || this.editingInstruction.publishForOperators === null) {
        this.editingInstruction.publishForOperators = true;
      }

      this.originalInstruction = _.cloneDeep(this.editingInstruction);

      for (let i = 0; i < 5; i++) {
        this.tempTagArrays[`key${i}`] = _.map(this.editingInstruction[`key${i}`], value => {
          return {key: value, value}
        });
      }
      this.fetchCreatedByUser();
      if (this.editingInstruction.updated_by) {
        this.getUpdatedByUser();
      }
      if (this.getConfig.publications && this.editingInstruction.id) {
        pmtApi.get(
          '/instructions/versions/' + this.editingInstruction.id,
        ).then(async res => {
          const versions = res.data || [];
          for (const version of versions) {
            if (version.created_by) {
              try {
                const userRes = await this.$FUNCTIONS.Users("getUserById", version.created_by);
                version.created_by_name = userRes.data.displayName || userRes.data.email;
              } catch (e) {
                version.created_by_name = null;
              }
            }
          }
          this.versions = versions;
        }).catch(e => {
          console.error("Failed to fetch instruction versions", e);
        });
      }
    }
    this.fetchPossibleValues();
  }
}
</script>

<style scoped>

.btn-full-width {
  width: 100%;
}

#instruction-title {
  width: 100%;
}

.key-selector {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.link-editor {
  margin-top: 4rem;
}

#count_message[limitReached='true'] {
  background: #ff9595;
}

#count_message {
  margin-top: -2.6rem;
  margin-right: 5px;
  position: absolute;
  right: 2rem;
  font-size: 1.1rem;
  background: #b9b9b9;
  font-weight: bold;
  color: white;
  padding: 1px 8px;
  border-radius: 24px;
}

.left-side {
  padding: 0px;
}

.drag-drop-container {
  margin-bottom: 1rem;
}

.updated_by, .created_by {
  font-size: 1.3rem;
  margin-left: 3rem;
}

.file-owners {
  text-align: right;
  margin-top: 2rem;
}

.form-mutation-buttons {
  margin-top: 2rem;
}

.file-preview {
  height: 1050px;
  max-height: 1050px;
}

.loading-tags {
  position: absolute;
  z-index: 9999999;
  height: 41px;
  right: 15px;
  width: 3rem;
}

.publication-settings {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #DEE2E6;
}

.publication-settings__title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 0.5rem;
}

.publication-settings__tooltip {
  font-style: italic;
  color: #000;
  font-size: 1.2rem;
  margin-bottom: 1rem;
}

.publication-settings__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.publication-settings__toggle-label {
  font-weight: 600;
  font-size: 1rem;
  color: #272B30;
}

.instruction-history {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #DEE2E6;
}

.instruction-history__title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 0.75rem;
}

.instruction-history__list {
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: 400px;
  overflow-y: auto;
}

.instruction-history__entry {
  font-size: 1rem;
  color: #6A7178;
  line-height: 1.6;
  padding: 0.75rem 0;
  border-bottom: 1px solid #EAECEF;
}

.instruction-history__entry:last-child {
  border-bottom: none;
}

.instruction-history__entry-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.instruction-history__date {
  color: #6A7178;
}

.instruction-history__status {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 2px;
}

.instruction-history__status--published {
  background: #d7f8ff;
  color: #007a99;
}

.instruction-history__status--draft {
  background: #f0f0f0;
  color: #6A7178;
}

.instruction-history__changelog {
  color: #6A7178;
  display: block;
  margin-top: 2px;
}

.instruction-history__empty {
  font-size: 1.2rem;
  color: #6A7178;
  font-style: italic;
}

.publication-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
}

.publication-modal__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
}

.publication-modal__title {
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 20px;
  line-height: 1.4;
  letter-spacing: 0.25px;
  color: #000;
  margin: 0;
}

.publication-modal__description {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.25px;
  color: #6A7178;
  margin: 0;
}

.publication-modal__changelog {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.publication-modal__changelog-label {
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.25px;
  color: #6A7178;
  margin: 0;
}

.publication-modal__changelog-input {
  width: 100%;
  min-height: 175px;
  padding: 10px;
  border: 1px solid #6A7178;
  border-radius: 8px;
  background: #fff;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0.25px;
  color: #000;
  resize: vertical;
}

.publication-modal__changelog-input:focus {
  outline: none;
  border-color: #2A52BE;
}

.publication-modal__actions {
  display: flex;
  gap: 32px;
  align-items: center;
}

.publication-modal__actions .motivate-primary-button {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>

<style>
/* Overrule the loading spinner to make it smaller*/
.loading-tags > i {
  font-size: 2rem;
  top: 10px;
  left: -5px;
  position: absolute;
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
}

#publication-modal .modal-content {
  background: #F8F9FA;
}

#publication-modal .modal-header {
  display: none;
}
</style>